<template>
  <UIDialog
    :is-open
    :title="`Account Ownership for ${assetName}`"
    :z-index
    size="md"
    flat
    @close="handleClose"
  >
    <div class="precisions">
      <div class="precisions__actions">
        <div class="precisions__actions-buttons">
          <UIButton
            :leading="ArrowDownTrayIcon"
            label="Import"
            variant="gray"
            fill="light"
            size="sm"
            :disabled="isProcessing"
            @click="handleImport"
          />
          <UIButton
            :leading="ArrowUpTrayIcon"
            label="Export"
            variant="gray"
            fill="light"
            size="sm"
            :disabled="isProcessing"
            @click="handleExport"
          />
        </div>
        <UIButton
          v-if="listValues.length > 0"
          label="Owner"
          variant="secondary"
          size="sm"
          :leading="PlusIcon"
          :disabled="isProcessing"
          @click="handleAddOwner"
        />
      </div>
      <UIGrid
        ref="gridRef"
        :columns
        :items
        :footer
        editable
        :readonly="isProcessing"
        :value-paster
      >
        <template #cellAccountId="{ columnName, item }">
          <UIDropdownSearchList
            v-if="!item[columnName]"
            :data="listValues"
            placement="bottom-start"
            @click:item="item[columnName] = $event"
          >
            <template #default="{ show }">
              <UIButton
                label="Select owner"
                size="sm"
                variant="gray"
                fill="outlined"
                class="w-full"
                :trailing="ChevronDownIcon"
                @click="show"
              />
            </template>
          </UIDropdownSearchList>
        </template>
        <template #cellAmount="{ columnName, item, editCell }">
          <UIInputPercent
            v-if="editCell"
            :model-value="item[columnName]"
            size="sm"
            @click:enter="handleEditClickEnter"
            @click:tab="handleEditClickTab"
            @click:escape="handleEditClickEscape"
            @update:model-value="item[columnName] = $event"
            @ready="handleReady"
          />
        </template>
        <template #actions="{ rowIndex }">
          <UIButton
            v-if="rowIndex >= oldLength"
            v-tooltip.top="'Delete owner'"
            variant="danger"
            fill="light"
            size="sm"
            :leading="TrashIcon"
            @click="handleDeleteOwner(rowIndex)"
          />
        </template>
        <template #footer="{ columnName, displayValue }">
          <template v-if="columnName === 'accountId'">
            {{ displayValue }}
          </template>
          <span
            v-else
            :class="totalClasses"
          >
            {{ displayValue }}
          </span>
        </template>
      </UIGrid>
      <UIButtonsGroup>
        <template #default="{ short }">
          <UIButtonClose
            :short
            @click="handleClose"
          />
          <UIButtonSave
            :short
            label="Update"
            type="submit"
            @click="handleComplete"
          />
        </template>
      </UIButtonsGroup>
    </div>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, markRaw, ref, useTemplateRef, watch } from 'vue'
import { uniqBy } from 'lodash'

import { DropdownListData, ModalClass, TransactionSettings } from '@types'
import { OrganizeFamilyStructureAmountItem, OrganizeFamilyStructurePath } from '../utils/types'
import { TransactionAmountRule } from '@/entities/transactions/utils/enums'

import { ASSET_FIELD, UNKNOWN_VALUE } from '@/const'

import { displayAmount } from '@/entities/transactions/utils/helpers'
import { exportCSV } from '@/helpers/common'
import { numberParse } from '@/helpers/numbers'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'

import { UIDialog, UIImportDialog } from '@ui/modals'
import { UIButton, UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import { ArrowDownTrayIcon, ArrowUpTrayIcon, ChevronDownIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { UIGrid } from '@ui/grid'
import { UIInputPercent } from '@ui/fields'
import { UIDropdownSearchList } from '@ui/dropdowns'
import { generateId } from '@/entities/utils/helpers'

type Props = {
  modal: ModalClass<any>
  entrySettings: TransactionSettings['entries'][0]
  assetId: string
  paths: OrganizeFamilyStructurePath[]
  data: DropdownListData[]
}

type Emits = {
  'complete': [data: OrganizeFamilyStructureAmountItem[]]
  'cancel': []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const assetsBunchStore = useAssetsBunchStore()
const modalsStore = useModalsStore()

const gridRef = useTemplateRef('gridRef')

const items = ref<OrganizeFamilyStructureAmountItem[]>([])
const oldLength = ref(0)

const isProcessing = ref(false)

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const assetName = computed(() => 
  assetsBunchStore.getElementById(props.assetId)?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE
)

const accountTitle = computed(() => props.entrySettings.account?.title || 'Owner')

const columns = computed(() => [
  {
    name: 'accountId',
    caption: accountTitle.value,
    formatter: (value: string) => accountFormatter(value),
    unsortable: true,
    uneditable: true,
  },
  {
    name: 'amount',
    caption: 'Percentage',
    formatter: (value: number) => amountFormatter(value),
    unsortable: true,
  }
])

const totalAmount =  computed(() => {
  return items.value.reduce((acc, value) => {
    return acc + (value.amount || 0)
  }, 0)
})

const footer = computed(() => ({
  accountId: 'Total',
  amount: amountFormatter(totalAmount.value),
}))

const listValues = computed(() => {
  const filtered = props.data
    .filter(item => item.value !== props.assetId && !items.value.some(i => i.accountId === item.value))
  return uniqBy(filtered, 'value')
})

const totalClasses = computed(() => ({
  'precisions__total--positive':  totalAmount.value === 1,
  'precisions__total--negative':  totalAmount.value !== 1,
}))

const accountFormatter = (accountId: string) => {
  return assetsBunchStore.getElementById(accountId)?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE
}

const amountFormatter = (value: number | null | undefined) => {
  const settings = props.entrySettings.amount
  if (!settings) return ''
  const isPercent = settings?.rule === TransactionAmountRule.PERCENT
  return displayAmount(value, isPercent)
}

const handleReady = (e: HTMLInputElement) => {
  e.focus()
}

const handleEditClickEnter = async (event: Event) => {
  event.stopPropagation()
  gridRef.value?.release()
  gridRef.value?.clickEnter()
}

const handleEditClickTab = async (event: Event) => {
  event.stopPropagation()
  event.preventDefault()
  gridRef.value?.release()
  gridRef.value?.clickTab()
}

const handleEditClickEscape = (event: Event) => {
  event.stopPropagation()
  gridRef.value?.release()
}

const handleClose = () => {
  emit('cancel')
  props.modal.close()
}

const handleComplete = () => {
  emit('complete', items.value)
  props.modal.close()
}

const handleAddOwner = () => {
  items.value.push({ id: generateId(), accountId: undefined, amount: null })
  gridRef.value?.edit(items.value.length, 1)
}

const handleDeleteOwner = (index: number) => {
  items.value.splice(index, 1)
}

const handleImport = () => {
  const modalInstance = modalsStore.init('contacts-import', markRaw(UIImportDialog))
  modalInstance?.open(modalsStore.getZIndex(), {
    columns,
    title: 'Import account ownership',
    headersExample: 'account,amount',
    onImportStart: handleImportStart,
    onImportStep: handleImportStep,
    onImportComplete: handleImportComplete,
  })
}

const handleImportStart = () => {
  isProcessing.value = true
}

const handleImportStep = ({ data }: { data: any; index: number }) => {
  const accountId = assetsBunchStore.getElementByName(data.account)?.id
  const parsedAmount = numberParse(data.amount)
  const amount = Number.isFinite(parsedAmount) ? parsedAmount : null
  items.value.push({ id: generateId(), accountId, amount })
}

const handleImportComplete = () => {
  isProcessing.value = false
}

const handleExport = () => {
  const cols = ['account', 'amount']
  const headers = Object.fromEntries(cols.map(col => [col, col]))
  const data = items.value.map(item => {
    const account = item.accountId ? accountFormatter(item.accountId) : ''
    return { account, amount: item.amount }
  })
  exportCSV(`${assetName.value}-precision`, cols, [headers, ...data], false)
}

const valuePaster = (
  field: string,
  item: any,
  value: number | string,
) => {
  switch (field) {
    case 'amount':
      const amount = numberParse(`${value}`.replace('%', '')) / 100
      item.amount = amount
      break
    case 'accountId':
      const accountId = assetsBunchStore.getElementByName(value as string)?.id
      item.accountId = accountId
      break
    default:
      break
  }
}

watch(() => props.paths, value => {
  items.value = uniqBy(
    value
      .filter(el => el.path.at(-1) === props.assetId)
      .map(el => ({ id: generateId(), accountId: el.path.at(-2), amount: el.amount })), 
    'accountId'
  )
  oldLength.value = items.value.length
}, { immediate: true })
</script>

<style scoped>
.precisions {

  &__actions {
    @apply flex justify-between;
    @apply p-3;
  }

  &__total {

    &--positive {
      @apply text-gray-950 dark:text-green-400;
    }
    &--negative {
      @apply text-red-600 dark:text-red-400;
    }

    & + & {
      @apply col-span-2;
    }
  }

  .ui-buttons-group {
    @apply py-4 px-default;
  }
}
</style>
